"use client";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useLanguage } from "@/components/LanguageProvider";
import type { PageSection } from "@/lib/api";
import {
  maintenanceShowLogo,
  resolveMaintenanceLogoSrc,
  type MaintenanceLogoFields,
} from "@/lib/maintenance-logo";
import type { SitePayload } from "@/lib/types";
import { renderCmsContent } from "@/lib/utils";
import { useEffect, useMemo, useState } from "react";

export type MaintenanceData = MaintenanceLogoFields & {
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  content?: string;
  background_color?: string;
  text_color?: string;
  accent_color?: string;
};

type Props = {
  data?: MaintenanceData;
  section?: PageSection;
  site?: SitePayload;
  /** Pre-resolved on the server when available (avoids client/env edge cases). */
  logoSrc?: string;
};

function fieldStr(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function mergeMaintenanceData(
  section: PageSection | undefined,
  dataProp: MaintenanceData | undefined,
  lang: string,
): MaintenanceData {
  const base = (section?.data ?? dataProp ?? {}) as Record<string, unknown>;
  const ar =
    lang === "ar" && section?.dataAr
      ? (section.dataAr as Record<string, unknown>)
      : null;

  return {
    eyebrow: fieldStr(ar?.eyebrow ?? base.eyebrow),
    heading: fieldStr(ar?.heading ?? base.heading),
    subheading: fieldStr(ar?.subheading ?? base.subheading),
    content: fieldStr(ar?.content ?? base.content),
    background_color: fieldStr(base.background_color),
    text_color: fieldStr(base.text_color),
    accent_color: fieldStr(base.accent_color),
    logo_url: fieldStr(base.logo_url),
    show_logo: base.show_logo as string | boolean | undefined,
  };
}

export function MaintenanceSection({
  data: dataProp,
  section,
  site,
  logoSrc: logoSrcProp,
}: Props) {
  const { lang, mode } = useLanguage();
  const data = useMemo(
    () => mergeMaintenanceData(section, dataProp, lang),
    [section, dataProp, lang],
  );

  const [safeContent, setSafeContent] = useState("");
  const raw = data.content ?? "";

  useEffect(() => {
    let cancelled = false;
    void renderCmsContent(raw).then((html) => {
      if (!cancelled) setSafeContent(html);
    });
    return () => {
      cancelled = true;
    };
  }, [raw, lang]);

  const bg = data.background_color?.trim() || "#0f172a";
  const text = data.text_color?.trim() || "#f8fafc";
  const accent = data.accent_color?.trim() || "#38bdf8";
  const showLogo = maintenanceShowLogo(data.show_logo);
  const logoSrc =
    logoSrcProp?.trim() ||
    resolveMaintenanceLogoSrc(data, site);

  return (
    <section
      className="relative flex min-h-[100dvh] flex-col"
      style={{ backgroundColor: bg, color: text }}
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      {mode === "both" ? (
        <div className="absolute end-4 top-4 z-10">
          <LanguageSwitcher className="border-white/40 text-white hover:border-white hover:text-white" />
        </div>
      ) : null}
      {showLogo && logoSrc ? (
        <header className="flex w-full shrink-0 justify-center px-4 pb-2 pt-10 md:pt-12">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={logoSrc}
            alt={site?.siteName ?? "Logo"}
            width={200}
            height={64}
            className="h-14 w-auto max-w-[min(100%,14rem)] object-contain md:h-16"
            decoding="async"
            fetchPriority="high"
          />
        </header>
      ) : null}
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col items-center justify-center px-4 py-8 text-center">
        {data.eyebrow ? (
          <p
            className="mb-3 text-xs font-bold uppercase tracking-[0.2em]"
            style={{ color: accent }}
          >
            {data.eyebrow}
          </p>
        ) : null}
        {data.heading ? (
          <h1 className="text-balance text-3xl font-extrabold tracking-tight md:text-4xl">
            {data.heading}
          </h1>
        ) : null}
        {data.subheading ? (
          <p className="mt-4 text-pretty text-base leading-relaxed opacity-90 md:text-lg">
            {data.subheading}
          </p>
        ) : null}
        {safeContent ? (
          <div
            className={`prose-cms mt-6 text-sm leading-relaxed opacity-90 [&_a]:underline ${lang === "ar" ? "text-right" : "text-left"}`}
            style={{ color: text }}
            dangerouslySetInnerHTML={{ __html: safeContent }}
          />
        ) : null}
      </div>
    </section>
  );
}
